export const DEVICE_ORIENTATION_EVENT = "mt:deviceorientation";

export type OrientationGate = "portrait" | "landscape_allowed";
export type DeviceOrientation = "portrait" | "landscape";

type TauriInvoke = (
  command: string,
  args?: Record<string, unknown>,
) => Promise<unknown>;

function tauriInvoke(): TauriInvoke | null {
  const internals = (
    window as Window & {
      __TAURI_INTERNALS__?: { invoke?: TauriInvoke };
    }
  ).__TAURI_INTERNALS__;
  return typeof internals?.invoke === "function" ? internals.invoke : null;
}

/**
 * Gate the native shells' interface orientation (PLAN §"Racing V2 interface"
 * → Mobile orientation contract). The shells boot portrait-locked and only
 * the race fullscreen opens landscape; the racing viewer calls this on
 * fullscreen enter/exit/unmount. Ordinary browsers have no
 * `__TAURI_INTERNALS__` bridge, so this is a no-op on mobile/desktop web —
 * exactly the intended scope (web orientation cannot be locked outside
 * fullscreen, and inside fullscreen both orientations must stay usable).
 */
export function setOrientationGate(gate: OrientationGate): void {
  const invoke = tauriInvoke();
  if (!invoke) return;
  void invoke("set_orientation_gate", { gate }).catch(() => {
    // Older shell builds without the command: the app simply keeps its
    // Info.plist/manifest behavior. Never surface this to the player.
  });
}

/**
 * Parse a native `mt:deviceorientation` event (TiltRacingOrientation.swift /
 * MainActivity.kt). The shells report the PHYSICAL sensor, which keeps
 * delivering rotations while the interface itself is portrait-locked —
 * the only rotation signal the racing page gets inside the app.
 */
export function deviceOrientationFrom(event: Event): DeviceOrientation | null {
  if (!(event instanceof CustomEvent)) return null;
  const value = (event.detail as { orientation?: unknown } | null)?.orientation;
  return value === "portrait" || value === "landscape" ? value : null;
}
